import { ActionReference, GameObjectReference, GameState } from "@shared/types";
import { LitElement, TemplateResult, css, html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { getState, performAction } from "../services/routeService";
import {unsafeHTML} from "lit/directives/unsafe-html.js";

@customElement("game-canvas")
export class GameCanvas extends LitElement {
    public static styles = css`
        .game {
            height: 100%;
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: auto auto 1fr auto;
            grid-column-gap: 0px;
            grid-row-gap: 0px;
        }

        .title {
            text-align: center;
            margin-top: 10px;
        }

        .header {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex-grow: 1;
            position: relative;
            margin-top: 10px;
        }

        .header img {
            width: 100%;
            height: auto;
            image-rendering: pixelated;
        }

        .header img:nth-child(n + 2) {
            position: absolute;
        }

        .content {
            flex-grow: 1;
            overflow: auto;
            margin-top: 10px;
            padding: 0 10px;
        }

        .content p {
            margin: 0 0 10px 0;
        }

        .content p:last-of-type {
            margin: 0;
        }

        .footer {
            background-color: #1f1f1f;
            border: 2px solid #585858;
            margin-top: 10px;
            display: flex;
            height: 120px;
        }

        .footer .buttons {
            display: flex;
            flex-direction: column;
            overflow: auto;
            padding: 10px 10px 0 10px;
        }

        .footer .button {
            background-color: #f8a514;
            border: 1px solid #8c6c19;
            color: #63430c;
            padding: 5px 10px;
            margin: 0 0 10px 10px;
            text-transform: uppercase;
            cursor: pointer;
            display: inline-block;
            user-select: none;
        }

        .footer .button.active,
        .footer .button:hover {
            background-color: #c98716;
        }
    `;

    private roomTitle?: string;
    private roomImages?: string[];
    private contentText?: string[];
    private actionButtons?: ActionReference[];
    private gameObjectButtons?: GameObjectReference[];
    private gameInventoryButtons?: GameObjectReference[];
    private goToButtons?: GameObjectReference[];

    private selectedActionButton?: ActionReference;
    private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    public connectedCallback(): void {
        super.connectedCallback();

        void this.refreshState();
    }

    private async refreshState(): Promise<void> {
        const state: GameState = await getState();

        this.updateState(state);
    }

    private updateState(state: GameState): void {
        //Reset the component
        this.roomTitle = state.roomTitle;
        this.roomImages = state.roomImages;
        this.contentText = state.text;
        this.actionButtons = state.actions;
        this.gameObjectButtons = state.objects;
        this.gameInventoryButtons = state.usableItems;
        this.goToButtons = state.adjacentRooms;

        this.selectedActionButton = undefined;
        this.selectedGameObjectButtons.clear();

        this.requestUpdate();
    }

    private async handleClickAction(button: ActionReference): Promise<void> {
        if (button.needsObject) {
            this.selectedActionButton = button;
            this.selectedGameObjectButtons.clear();

            this.requestUpdate();
        } else {
            const state: any = await performAction(button.alias);

            if (state === undefined) {
                return;
            }

            this.updateState(state);
        }
    }

    private async handleClickObject(button: GameObjectReference): Promise<void> {
        if (!this.selectedActionButton) {
            return;
        }

        this.selectedGameObjectButtons.add(button);

        const state: GameState | undefined = await performAction(
            this.selectedActionButton.alias,
            [...this.selectedGameObjectButtons].map((e) => e.alias)
        );

        if (this.selectedGameObjectButtons.size >= 2) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        this.requestUpdate();

        if (state === undefined) {
            return;
        }

        this.updateState(state);
    }

    protected render(): TemplateResult {
        return html`
            <div class="game">
                ${this.renderTitle()} ${this.renderHeader()} ${this.renderContent()} ${this.renderFooter()}
            </div>
        `;
    }

    private renderTitle(): TemplateResult {
        if (this.roomTitle) {
            return html`<div class="title">${this.roomTitle}</div>`;
        }

        return html`${nothing}`;
    }

    private renderHeader(): TemplateResult {
        if (this.roomImages && this.roomImages.length > 0) {
            return html`
                <div class="header">
                    ${this.roomImages?.map((url) => html`<img src="/assets/img/rooms/${url}.png" />`)}
                </div>
            `;
        }

        return html`${nothing}`;
    }

    private renderContent(): TemplateResult {
        // Render each line of content with the special syntax parsed
        return html`<div class="content">${this.contentText?.map(line => this.styleText(line, true))}</div>`;
    }
    
    
    
    

    private chooseGameButtons(): GameObjectReference[] | undefined {
        if(this.selectedActionButton?.alias === "use-action") {
            return this.gameInventoryButtons;
        }
        else if(this.selectedActionButton?.alias === "goto-action"){
            return this.goToButtons;
        }
        else {
            return this.gameObjectButtons;
        }
    }

    private renderFooter(): TemplateResult {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this.actionButtons?.map(
                            (button) => html`<a
                                class="button ${this.selectedActionButton === button ? "active" : ""}"
                                @click=${(): void => void this.handleClickAction(button)}
                                >${this.styleText(button.label)}</a
                            >`
                        )}
                    </div>
                    <div>
                        ${this.selectedActionButton
                            ? this.chooseGameButtons()?.map(
                                  (button) => html`<a
                                      class="button ${this.selectedGameObjectButtons.has(button)
                                          ? "active"
                                          : ""}"
                                      @click=${(): void => void this.handleClickObject(button)}
                                      >${this.styleText(button.name)}</a
                                  >`
                              )
                            : nothing}
                    </div>
                </div>
            </div>
        `;
    }

    private styleText(text: string, needsSpacing?: boolean): TemplateResult {
        // Regular expression to match the syntax: [?]??[/?]. ? is variable amount of characters
        const regex: RegExp = /\[(.*?)\](.*?)\[\/(.*?)\]/g;
    
        /**
        * Replace the special syntax with HTML markup for styling
        * @param _match The entire string selected by the regex. (incl. tags) [?]??[/?]
        * @param openTag The string between the first [Tag]??[/Tag]
        * @param content The string between the tags [?]content[/?]
        */
        const parsedText: string = text.replace(regex, (_match: string, tag: string, content: string) => {
            switch (tag) {
                // Add more cases for other supported colors or styles
                case "dialogue":
                    return `<span style="color: #fcba03;">${content}</span>`;
                case "room":
                    return `<span style="color: #870e0e;">${content}</span>`;
                case "character":
                    return `<span style="color: #3654ff;">${content}</span>`;
                case "stamina":
                    return `<span style="color: #6ea5ff;">${content} <img src="/assets/img/stamina.png" alt="stamina" height="18px" style="transform: translate(-6px, 2px);"></span>`;
                case "health":
                    return `<span style="color: #eb4034;">${content} <img src="/assets/img/health.png" alt="stamina" height="18px" style="transform: translate(-4px, 1px)"></span>`;
                case "hit":
                    return `<span style="color: #a60b00;">${content} <img src="/assets/img/hit.png" alt="stamina" height="18px" style="transform: translate(-6px, 2px)"></span>`;
                case "mitigate":
                    return `<span style="color: #116315;">${content} <img src="/assets/img/mitigate.png" alt="stamina" height="18px" style="transform: translate(-4px, 2px)"></span>`;
                case "you":
                    return `<span style="color: #fcba03;">${content}</span>`;
                case "gradient":
                    return `<span style="color: #fff; text-shadow: 0 0 5px #fcba03, 0 0 10px #fcba03, 0 0 15px #fcba03, 0 0 20px #fcba03; font-weight: bold;">${content}</span>`;
                case "overlay":
                    return `<span style="color: red; text-shadow: 0 0 5px red, 0 0 10px red, 0 0 15px red, 0 0 20px red; font-weight: bold;">${content}</span>`;
                default:
                    return content;
            }
        });
        
        if(needsSpacing === true) {
            return html`${unsafeHTML("<p>" + parsedText + "</p>")}`;
        }
        else {
            return html`${unsafeHTML(parsedText)}`;
        }
    }
}